import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';

import { debugAuth } from './debug';
import { roleGuardOutcome, sanitizeReturnTo } from './roleDecision';
import { useCurrentRole } from './useCurrentRole';

interface Props {
  /** The role this route is intended for. The other role gets redirected. */
  audience: 'practitioner' | 'customer';
  /** The page content. Rendered only when the current user matches. */
  children: ReactNode;
}

/**
 * Frontend route guard. The actual security boundary is the backend's
 * `require_practitioner` / `_require_portal_user` deps — this guard is
 * UX, not a security gate: it bounces the wrong-role user to their own
 * landing so they don't see a broken page hitting 403 on every API call.
 *
 * Behavior:
 * - Clerk hasn't loaded yet → render nothing briefly.
 * - Signed out on a PRACTITIONER route → redirect to /sign-in, carrying the
 *   current location as `?redirect=` so the post-sign-in dispatch lands the
 *   user back on the deep link they came for (INP-626). Those pages have no
 *   signed-out UI — rendering them left the page spinning forever.
 * - Signed out on the customer route (/portal) → render: the portal shows
 *   its own signed-out auth, including the Clerk invitation SignUp that
 *   reads the `__clerk_ticket` from the URL. Bouncing would drop the ticket
 *   and dead-end a freshly-invited customer (INP-703).
 * - Wrong role (data-confirmed from Clerk JWT claims) → redirect.
 * - Right role → render children.
 *
 * Why no DB lookup: role is derived from Clerk's session claims
 * (email domain → practitioner; org_id → customer). The previous
 * `/api/me`-backed shape caused multiple production bugs that all
 * traced back to either token-cache staleness or unexplained UI
 * mis-reads of correct backend responses. Removing the chokepoint
 * removes the bug class.
 */
export function RoleGuard({ audience, children }: Props) {
  const role = useCurrentRole();
  const navigate = useNavigate();
  // Path + search of where the user is right now — what sign-in should
  // return them to. `href` is TanStack's already-assembled relative URL.
  const currentHref = useRouterState({ select: (s) => s.location.href });
  // Redirect ONLY on a positively-confirmed wrong audience — an as-yet-unknown
  // role (the transient state during a Clerk session refresh) renders through
  // instead of bouncing. See roleGuardOutcome for the invariant (INP-502).
  const outcome = roleGuardOutcome(audience, role);

  useEffect(() => {
    if (debugAuth()) {
      // eslint-disable-next-line no-console
      console.log('[auth.RoleGuard] effect', {
        audience,
        outcome,
        isAuthReady: role.isAuthReady,
        isSignedIn: role.isSignedIn,
        isPractitioner: role.isPractitioner,
        isCustomer: role.isCustomer,
        email: role.email,
        orgId: role.orgId,
        path: typeof window !== 'undefined' ? window.location.pathname : null,
      });
    }
    if (outcome === 'redirect-sign-in') {
      const returnTo = sanitizeReturnTo(currentHref);
      void navigate({
        to: '/sign-in',
        search: returnTo ? { redirect: returnTo } : {},
        replace: true,
      });
    } else if (outcome === 'redirect-portal') {
      void navigate({ to: '/portal', replace: true });
    } else if (outcome === 'redirect-customers') {
      void navigate({ to: '/customers', replace: true });
    }
  }, [audience, outcome, role, navigate, currentHref]);

  if (outcome !== 'render') return null;
  return <>{children}</>;
}
